import { fetcher } from './apiClient';

/**
 * The shape of the API response
 */
type GetUserResponse = {
    id: string;
    name: string;
    email: string;
    avatarUrl: string | null;
    orgId: string;
    createdAt: string;
    updatedAt: string;
};

type GetMeResponse = {
    user: GetUserResponse;
    organization: { id: string; name: string; avatarUrl?: string | null };
    activeOrg: {
        id: string;
        name: string;
        avatarUrl?: string | null;
        isInternal?: boolean;
    } | null;
    isStaff: boolean;
    /** True only for the 'Watchly Super Admin' platform role; isStaff also covers account managers. */
    isSuperAdmin: boolean;
    permissions: string[];
    profile: {
        id: string;
        name: string;
        smsNumber: string;
        email: string;
        avatarFilename: string;
        createdAt: string;
        updatedAt: string;
        dispatchCenterId: string|null;
        agencyId: string|null;
        userId: string|null;
    }
};

/**
 * Our internal representation of a User, decoupled from the API response shape
 */
type User = {
    id: string;
    name: string;
    orgId: string;
    avatarUrl: string | null;
};

type Organization = {
    id: string;
    name: string;
    avatarUrl: string | null;
};

type ActiveOrg = {
    id: string;
    name: string;
    avatarUrl?: string | null;
    /** Whether the active org is Watchly's own (internal) org rather than a customer. */
    isInternal: boolean;
} | null;

type Profile = {
    id: string;
    name: string;
    smsNumber: string|null;
    email: string;
};

// const mockUserResponse: GetUserResponse = {}

const coerceApiResponseIntoEntity = (apiResponse: GetUserResponse): User => {
    return {
        id: apiResponse.id,
        name: apiResponse.name,
        orgId: apiResponse.orgId,
        avatarUrl: apiResponse.avatarUrl ?? null,
    };
};

const coerceProfileIntoEntity = (
    apiResponse: GetMeResponse,
): Profile => {
    return {
        id: apiResponse.profile.id,
        name: apiResponse.profile.name,
        smsNumber: apiResponse.profile.smsNumber,
        email: apiResponse.profile.email
    };
};

const coerceOrganizationIntoEntity = (
    apiResponse: GetMeResponse,
): Organization => {
    return {
        id: apiResponse.organization.id,
        name: apiResponse.organization.name,
        avatarUrl: apiResponse.organization.avatarUrl ?? null,
    };
};

/**
 * The GET /api/me route returns the current authenticated user.
 * It's essentially a variation of the /api/user/{userId} route.
 * The shape of the response matches the shape of a response from the GET /api/user/{userId} route.
 *
 * @throws AxiosError
 */
const meFetcher = async (): Promise<{
    user: User;
    organization: Organization;
    activeOrg: ActiveOrg;
    isStaff: boolean;
    isSuperAdmin: boolean;
    permissions: string[];
}> => {
    const response = await fetcher<GetMeResponse>(`api/me`);

    return {
        user: coerceApiResponseIntoEntity(response.user),
        organization: coerceOrganizationIntoEntity(response),
        // Default isInternal to false so internal-only chrome fails closed if
        // the field is ever missing from the payload.
        activeOrg: response.activeOrg
            ? { ...response.activeOrg, isInternal: response.activeOrg.isInternal ?? false }
            : null,
        isStaff: response.isStaff ?? false,
        isSuperAdmin: response.isSuperAdmin ?? false,
        permissions: response.permissions ?? [],
    };
};

const userFetcher = async (userId: string): Promise<User> => {
    return coerceApiResponseIntoEntity(
        await fetcher<GetUserResponse>(`api/user/${userId}`),
    );
};

export { meFetcher, userFetcher };
export type { ActiveOrg, GetUserResponse, Organization, User };
